import React, { useState, useEffect, useRef } from 'react';
import {
  getPopupAlert,
  savePopupAlert,
} from '../services/popupAlertApiService.ts';
import './PopupAlertEditor.css';

interface PopupAlertEditorProps {
  storeId: string;
}

const PopupAlertEditor: React.FC<PopupAlertEditorProps> = ({ storeId }) => {
  // Form state
  const [message, setMessage] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [lastEdited, setLastEdited] = useState<Date | null>(null);
  const [lastAuthor, setLastAuthor] = useState<string>('');
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  // Load existing popup alert on mount or when storeId changes
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const currentAlert = await getPopupAlert(storeId);

        if (currentAlert) {
          setMessage(currentAlert.message);
          setIsEnabled(currentAlert.isEnabled);
          setLastEdited(currentAlert.lastEdited);
          setLastAuthor(currentAlert.lastAuthor);
        } else {
          // Reset form for stores with no alert configured
          setMessage('');
          setIsEnabled(false);
          setLastEdited(null);
          setLastAuthor('');
        }
      } catch (err) {
        console.error('Error loading popup alert data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [storeId]);

  // Handle save
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      await savePopupAlert(storeId, {
        message,
        isEnabled,
      });

      // Refetch to get accurate lastEdited/lastAuthor from server
      const savedAlert = await getPopupAlert(storeId);
      if (savedAlert) {
        setLastEdited(savedAlert.lastEdited);
        setLastAuthor(savedAlert.lastAuthor);
      }

      setSuccessMessage('Popup alert saved successfully!');

      // Clear success message after 3 seconds
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error saving popup alert:', err);
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="popup-alert-editor">
        <div className="popup-alert-loading">Loading popup alert configuration...</div>
      </div>
    );
  }

  return (
    <div className="popup-alert-editor">
      {error && <div className="popup-alert-error">{error}</div>}
      {successMessage && <div className="popup-alert-success">{successMessage}</div>}

      <div className="popup-alert-form">
        {/* Enable/Disable Toggle */}
        <div className="form-section">
          <label className="toggle-label">
            <span className="toggle-text">Enable Popup Alert</span>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </div>
            <span className={`toggle-status ${isEnabled ? 'enabled' : 'disabled'}`}>
              {isEnabled ? 'ON' : 'OFF'}
            </span>
          </label>
        </div>

        {/* Message Text */}
        <div className="form-section">
          <label className="form-label">
            Alert Message
            <span className="char-count">{message.length}/500</span>
          </label>
          <textarea
            className="message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter your popup alert message..."
            maxLength={500}
            rows={3}
          />
          <p className="form-hint">
            This alert will appear as a banner on the store info page, a blocking popup on the menu page
            (requires acknowledgment), and as a message in the cart.
          </p>
        </div>

        {/* Last Edited Info */}
        {lastEdited && (
          <div className="last-edited-info">
            Last edited: {lastEdited.toLocaleString()} by {lastAuthor}
          </div>
        )}

        {/* Save Button */}
        <div className="form-actions">
          <button
            className="save-button"
            onClick={handleSave}
            disabled={saving || (isEnabled && !message.trim())}
          >
            {saving ? 'Saving...' : 'Save Popup Alert'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PopupAlertEditor;
